import { ApiError, NetworkError } from '@/api/client';
import { NotFoundState } from './EmptyState';
import { useT, type TranslateFn } from '@/i18n/LanguageContext';

/**
 * Renders any thrown error as a clear message, a recovery action, and the
 * visible request_id a real backend error carries - never a raw stack
 * trace. A 404 from a portal read renders the dedicated NotFoundState
 * (see that component's own docstring) rather than a generic error banner,
 * since a cross-seller/manipulated-id lookup is an EXPECTED outcome here,
 * not a failure to alarm about.
 */
export function ErrorBanner({ error, onRetry }: { error: unknown; onRetry?: () => void }): JSX.Element {
  const t = useT();
  if (error instanceof ApiError && error.status === 404) {
    return <NotFoundState />;
  }
  if (error instanceof ApiError) {
    return (
      <div className="error-banner" role="alert">
        <div>
          <span className="code">{error.code}</span> — {describeCode(t, error.code, error.status)}
        </div>
        {onRetry && (
          <button type="button" onClick={onRetry} style={{ marginTop: 8 }}>
            {t('common.retry')}
          </button>
        )}
        <div className="rid">request_id: {error.requestId}</div>
      </div>
    );
  }
  if (error instanceof NetworkError) {
    return (
      <div className="error-banner" role="alert">
        <div>{error.message}</div>
        {onRetry && (
          <button type="button" onClick={onRetry} style={{ marginTop: 8 }}>
            {t('common.retry')}
          </button>
        )}
      </div>
    );
  }
  return (
    <div className="error-banner" role="alert">
      <div>{t('error.unexpected')}</div>
      {onRetry && (
        <button type="button" onClick={onRetry} style={{ marginTop: 8 }}>
          {t('common.retry')}
        </button>
      )}
    </div>
  );
}

function describeCode(t: TranslateFn, code: string, status: number): string {
  if (status === 401) return t('error.sessionExpired');
  if (status === 429) return t('error.tooManyRequests');
  if (code === 'INTERNAL.UNEXPECTED') return t('error.serverUnexpected');
  return t('error.requestFailed');
}
